/**
 * @file Require album folder price HOC (via album folder gotten from matched :albumFolderId).
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';

import { AlbumFolderWithCreatorBrandDetails } from '../../graphql/queries/album-folder';
import { albumFolderSubscriptionRequired } from '../../util/album-folder';

/**
 * Higher order component for the require album folder price functionality.
 *
 * @param {React.Component} WrappedComponent The component to be composed.
 * @returns {React.Component} The composed component.
 */
const WithRequireValidAlbumFolderForPurchase = WrappedComponent =>
  class RequireValidAlbumFolderForPurchase extends Component {
    static propTypes = {
      albumFolder: PropTypes.shape({
        id: PropTypes.string,
      }),
      loading: PropTypes.bool.isRequired,
    };

    render() {
      const { albumFolder, loading } = this.props;

      if (!loading && !albumFolder) {
        return <Redirect to="/not-found" />;
      }

      return !loading && !albumFolderSubscriptionRequired(albumFolder) ? (
        <Redirect to={`/discover/album-folder/${albumFolder.id}/faces`} />
      ) : (
        <WrappedComponent {...this.props} />
      );
    }
  };

/**
 * Higher order container for the require album folder price functionality.
 *
 * @param {React.Component} WrappedComponent The component to be composed.
 * @returns {Function} The HOC container.
 */
const RequireValidAlbumFolderForPurchase = WrappedComponent => {
  const WithValidAlbumFolder =
    WithRequireValidAlbumFolderForPurchase(WrappedComponent);

  // Use AlbumFolderWithCreatorBrandDetails as it is used in other
  // related album folder HOCs, so better if we try to cache it.
  const withAlbumFolderData = graphql(AlbumFolderWithCreatorBrandDetails, {
    options: ({ match }) => ({
      variables: {
        id: match.params.albumFolderId,
      },
    }),
    props: ({ data: { albumFolder, loading } }) => ({
      albumFolder,
      loading,
    }),
  });

  return compose(withAlbumFolderData)(WithValidAlbumFolder);
};

export { WithRequireValidAlbumFolderForPurchase };
export default RequireValidAlbumFolderForPurchase;
